import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { customersAPI } from "../../../api/client";
import { usePermission } from "../../../hooks/usePermission";
import useAuth from "../../../hooks/useAuth";
import MetricCardBase from "../components/MetricCard";
import StatusBadgeBase from "../components/StatusBadge";
import Toast from "../components/Toast";
import LockableButton from "../components/LockableButton";
import { verifyModuleEnabled } from "../utils/moduleAccess";
import "./POManagement.css";
import { activeStagesWithPart } from "../components/activeStageUtils";

const PAGE_SIZE = 20;

const STATUS_BADGE = {
  scheduled: { bg: "#eff6ff", color: "#1d4ed8", label: "Scheduled" },
  active:    { bg: "#dcfce7", color: "#15803d", label: "Active" },
  at_risk:   { bg: "#fff7ed", color: "#c2410c", label: "At Risk" },
  overdue:   { bg: "#fee2e2", color: "#dc2626", label: "Overdue" },
  completed: { bg: "#f1f5f9", color: "#475569", label: "Completed" },
};

const FILTER_TABS = [
  { key: "all", label: "All" },
  { key: "scheduled", label: "Scheduled" },
  { key: "active", label: "Active" },
  { key: "at_risk", label: "At Risk" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Completed" },
];

function MetricCard(props) {
  return <MetricCardBase className="pom-metric-card" {...props} />;
}

function StatusBadge({ status }) {
  return <StatusBadgeBase status={status} colorMap={STATUS_BADGE} />;
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatDeadline(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr + "T00:00:00");
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "long" });
  const year = date.getFullYear();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  let rel;
  if (diff === 0) rel = "today";
  else if (diff === 1) rel = "tomorrow";
  else if (diff === -1) rel = "yesterday";
  else if (diff > 0) rel = `${diff} days`;
  else rel = `${Math.abs(diff)} days ago`;
  return `${ordinal(day)} ${month}, ${year} (${rel})`;
}

function ActiveStagesCell({ po, status }) {
  const activeStages = activeStagesWithPart(po);
  if (activeStages.length === 0) {
    return <span style={{ color: "#94a3b8" }}>{status === "completed" ? "Done" : status === "scheduled" ? "Not started" : "—"}</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {activeStages.map((s) => (
        <div key={s.id}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: s.status === "overdue" ? "#dc2626" : "#475569", fontWeight: 600 }}>
            {s.status === "overdue" && <span title="Overdue">⚠️</span>}
            {s.partType && <span style={{ color: "#94a3b8", fontWeight: 600 }}>{s.partType} ·</span>}
            {s.stageName}
          </span>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
            {s.partsCompleted} done · {s.partsRemaining} remaining
          </div>
        </div>
      ))}
    </div>
  );
}

export default function POManagement() {
  const navigate = useNavigate();
  const canCreatePO = usePermission("manufacturing:po:create");
  const canUpdatePO = usePermission("manufacturing:po:update");
  const { enabledModules, setEnabledModules } = useAuth();
  const hasPOModule = !enabledModules || enabledModules.includes("po");
  const handleNewPOClick = async () => {
    const { allowed, message } = await verifyModuleEnabled("po", { enabledModules, setEnabledModules });
    if (!allowed) {
      setToast({ msg: message, type: "error" });
      return;
    }
    navigate("/manufacturing/po/new");
  };
  const [pos, setPos] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const searchTimerRef = useRef(null);
  const filterRef = useRef("all");
  const searchRef = useRef("");
  const customerRef = useRef("");
  const showArchivedRef = useRef(false);
  const pageRef = useRef(1);

  useEffect(() => {
    api.get("/manufacturing/pos/summary")
      .then((r) => setSummary(r.data.data || null))
      .catch(() => {});

    customersAPI.getAll()
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  const fetchPos = useCallback(async ({ pageNum, filterVal, searchVal, customerVal, includeArchivedVal, reset }) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { page: pageNum, limit: PAGE_SIZE };
      if (filterVal !== "all") params.status = filterVal;
      if (searchVal?.trim()) params.search = searchVal.trim();
      if (customerVal) params.customerId = customerVal;
      if (includeArchivedVal) params.includeArchived = "true";
      const res = await api.get("/manufacturing/pos", { params });
      const { data = [], hasMore: more = false } = res.data;
      setPos((prev) => reset ? data : [...prev, ...data]);
      setHasMore(more);
      pageRef.current = pageNum;
    } catch {
      if (reset) setPos([]);
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    filterRef.current = filter;
    customerRef.current = customerId;
    showArchivedRef.current = showArchived;
    fetchPos({ pageNum: 1, filterVal: filter, searchVal: searchRef.current, customerVal: customerId, includeArchivedVal: showArchived, reset: true });
  }, [filter, customerId, showArchived, fetchPos]);

  const handleSearchChange = (val) => {
    setSearch(val);
    searchRef.current = val;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchPos({ pageNum: 1, filterVal: filterRef.current, searchVal: val, customerVal: customerRef.current, includeArchivedVal: showArchivedRef.current, reset: true });
    }, 300);
  };

  const handleArchiveToggle = async (po) => {
    try {
      await api.patch(`/manufacturing/pos/${po.id}/${po.isActive ? "archive" : "unarchive"}`);
      setToast({ msg: po.isActive ? "PO archived" : "PO unarchived", type: "success" });
      fetchPos({ pageNum: 1, filterVal: filterRef.current, searchVal: searchRef.current, customerVal: customerRef.current, includeArchivedVal: showArchivedRef.current, reset: true });
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to update PO", type: "error" });
    } finally {
      setArchiveConfirm(null);
    }
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) {
          fetchPos({ pageNum: pageRef.current + 1, filterVal: filterRef.current, searchVal: searchRef.current, customerVal: customerRef.current, includeArchivedVal: showArchivedRef.current, reset: false });
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchPos]);

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <div className="pom-header">
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>PO Management</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Track and manage production orders.</p>
        </div>
        {canCreatePO && (
          <LockableButton
            className="btn-primary"
            style={{ whiteSpace: "nowrap" }}
            onClick={handleNewPOClick}
            locked={!hasPOModule}
            lockedTitle="Purchase Order module is not enabled for your company"
          >
            + New PO
          </LockableButton>
        )}
      </div>

      <div className="pom-metrics">
        <MetricCard label="Active POs" value={summary?.activePOs} />
        <MetricCard label="Scheduled" value={summary?.scheduledPOs} />
        <MetricCard label="Overdue Jobs" value={summary?.overdueJobs} accent={summary?.overdueJobs > 0 ? "#dc2626" : undefined} />
        <MetricCard label="Completed This Month" value={summary?.completedThisMonth} accent="#15803d" />
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div className="pom-toolbar">
          <div className="pom-filter-tabs">
            {FILTER_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                style={{
                  padding: "5px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
                  background: filter === t.key ? "#0f2d5c" : "transparent",
                  color: filter === t.key ? "#fff" : "#64748b",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select
            className="pom-customer-filter"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            aria-label="Filter by customer"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <input
            type="text"
            className="pom-search"
            placeholder="Search by PO number or customer…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : pos.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            {filter === "all" && !search && !customerId ? "No purchase orders yet. Create your first PO." : "No POs match your filter."}
          </div>
        ) : (
          <>
            {/* Desktop / tablet: scrollable table */}
            <div className="pom-table-wrap">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["PO Number", "Customer", "Parts", "Total Qty", "Workflow", "Active Stages", "Deadline", "Status"].map((h) => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                    {canUpdatePO && (
                      <th style={{ padding: "10px 16px", borderBottom: "1px solid #e4ebf5", width: 110 }}></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pos.map((po) => {
                    const partTypes = [...new Set((po.parts || []).map((p) => p.partType))].join(", ");
                    const totalQty = (po.parts || []).reduce((sum, p) => sum + p.quantity, 0);
                    // Per-part POs have no PO-level workflow — list the parts' own
                    const partWorkflows = [...new Set((po.parts || []).map((p) => p.workflow?.name).filter(Boolean))].join(", ");
                    const deadlineStr = po.deliveryDate ? po.deliveryDate.split("T")[0] : "";
                    const effectiveStatus = (deadlineStr && deadlineStr < todayStr && po.status !== "completed") ? "overdue" : po.status;

                    return (
                      <tr key={po.id} style={{ borderBottom: "1px solid #f1f5f9", background: effectiveStatus === "overdue" ? "#fff5f5" : "transparent", opacity: po.isActive === false ? 0.55 : 1 }}>
                        <td style={{ padding: "10px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Link to={`/manufacturing/po/${po.id}`} style={{ color: "#1d4ed8", fontWeight: 700, textDecoration: "none" }}>
                              {po.poNumber}
                            </Link>
                            {po.isActive === false && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>Archived</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 16px", color: "#0f2d5c", fontWeight: 600 }}>{po.customerName}</td>
                        <td style={{ padding: "10px 16px", color: "#64748b", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{partTypes || "—"}</td>
                        <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>{totalQty.toLocaleString()}</td>
                        <td style={{ padding: "10px 16px", color: "#64748b" }}>{partWorkflows || po.workflow?.name || "—"}</td>
                        <td style={{ padding: "10px 16px" }}>
                          <ActiveStagesCell po={po} status={po.status} />
                        </td>
                        <td style={{ padding: "10px 16px", color: effectiveStatus === "overdue" ? "#dc2626" : "#0f2d5c", fontWeight: effectiveStatus === "overdue" ? 700 : 400 }}>
                          {formatDeadline(deadlineStr)}
                        </td>
                        <td style={{ padding: "10px 16px" }}>
                          <StatusBadge status={effectiveStatus} />
                        </td>
                        {canUpdatePO && (
                          <td style={{ padding: "10px 16px", textAlign: "right" }}>
                            <button
                              type="button"
                              onClick={() => setArchiveConfirm(po)}
                              style={{ background: "none", border: `1px solid ${po.isActive ? "#fca5a5" : "#86efac"}`, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: po.isActive ? "#dc2626" : "#15803d" }}
                            >
                              {po.isActive === false ? "Unarchive" : "Archive"}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <div className="pom-po-cards">
              {pos.map((po) => {
                const activeStages = activeStagesWithPart(po);
                const partTypes = [...new Set((po.parts || []).map((p) => p.partType))].join(", ");
                const deadlineStr = po.deliveryDate ? po.deliveryDate.split("T")[0] : "";
                const effectiveStatus = (deadlineStr && deadlineStr < todayStr && po.status !== "completed") ? "overdue" : po.status;
                const deadlineRed = effectiveStatus === "overdue" || po.status === "at_risk";

                return (
                  <div key={po.id} className={`pom-po-card${effectiveStatus === "overdue" ? " overdue" : po.status === "at_risk" ? " at-risk" : ""}`} style={{ opacity: po.isActive === false ? 0.55 : 1 }}>
                    <div className="pom-po-card-top">
                      <Link to={`/manufacturing/po/${po.id}`} style={{ color: "#1d4ed8", fontWeight: 700, fontSize: 15, textDecoration: "none" }}>
                        {po.poNumber}
                      </Link>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {po.isActive === false && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>Archived</span>
                        )}
                        <StatusBadge status={effectiveStatus} />
                      </div>
                    </div>
                    <div className="pom-po-card-customer">{po.customerName}</div>
                    {partTypes && <div className="pom-po-card-meta">{partTypes}</div>}
                    {activeStages.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2 }}>
                        {activeStages.map((s) => (
                          <div key={s.id}>
                            <div style={{ fontSize: 12, color: s.status === "overdue" ? "#dc2626" : "#475569", fontWeight: 600 }}>
                              {s.status === "overdue" ? "⚠️ " : ""}{s.partType ? `${s.partType} · ` : ""}{s.stageName}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>
                              {s.partsCompleted} done · {s.partsRemaining} remaining
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {activeStages.length === 0 && (
                      <div className="pom-po-card-meta" style={{ color: "#94a3b8" }}>
                        {po.status === "completed" ? "Done" : po.status === "scheduled" ? "Not started" : "—"}
                      </div>
                    )}
                    {deadlineStr && (
                      <div className="pom-po-card-deadline" style={{ color: deadlineRed ? "#dc2626" : "#64748b", fontWeight: deadlineRed ? 700 : 400 }}>
                        Due {formatDeadline(deadlineStr)}
                      </div>
                    )}
                    {canUpdatePO && (
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          onClick={() => setArchiveConfirm(po)}
                          style={{ background: "none", border: `1px solid ${po.isActive ? "#fca5a5" : "#86efac"}`, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, color: po.isActive ? "#dc2626" : "#15803d" }}
                        >
                          {po.isActive === false ? "Unarchive" : "Archive"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ padding: "16px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading more…</div>
            )}
            {!hasMore && pos.length > 0 && (
              <div style={{ padding: "14px 0", textAlign: "center", color: "#cbd5e1", fontSize: 12 }}>
                All {pos.length} PO{pos.length !== 1 ? "s" : ""} loaded
              </div>
            )}
          </>
        )}
      </div>

      {archiveConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 360, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 8px", color: "#0f2d5c" }}>
              {archiveConfirm.isActive ? "Archive" : "Unarchive"} PO?
            </h3>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#64748b" }}>
              {archiveConfirm.isActive
                ? <>Archive <strong>{archiveConfirm.poNumber}</strong> ({archiveConfirm.customerName})? It will be hidden from the default PO list but its history and stage progress are preserved.</>
                : <>Unarchive <strong>{archiveConfirm.poNumber}</strong> ({archiveConfirm.customerName})? It will reappear in the default PO list.</>}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setArchiveConfirm(null)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #d6dfed", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={() => handleArchiveToggle(archiveConfirm)} style={{ padding: "8px 16px", borderRadius: 8, background: archiveConfirm.isActive ? "#dc2626" : "#15803d", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                {archiveConfirm.isActive ? "Archive" : "Unarchive"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
